// POST /api/ads/push-to-meta (liquidretail_backend routes/ads.js →
// services/metaAdsPushService.js). The batch body is
//   { pushed, failed, perAd: [{ adId, ok, metaAdId?, error?, status? }] }
// — `perAd`, not `results`. The same shape is what the /ads gallery types.
//
// A follow-up on the backend refuses individual ads with a per-ad status
// such as `already_pushed` (the existing Meta ad id rides on the row) or
// `missing_landing_url`. The message may arrive as `error`, `message`, or
// `reason`, and the machine status as `status` or `code`. This formatter
// prints whichever of those the row actually carries, so a new refusal
// shows up without a frontend change for that one code.

export type MetaPushPerAd = {
  adId?: string;
  ok?: boolean;
  status?: string | null;
  code?: string | null;
  metaSyncStatus?: string | null;
  metaAdId?: string | null;
  existingMetaAdId?: string | null;
  metaAdsetId?: string | null;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
};

export type MetaPushResponse = {
  pushed?: number;
  failed?: number;
  perAd?: MetaPushPerAd[] | null;
};

export type MetaPushTone = 'success' | 'warning' | 'error';

export type MetaPushInterpretation = {
  /** This ad was newly accepted by the push. */
  ok: boolean;
  /** The ad is on Meta (just pushed, or already there) and the UI should show it as exported. */
  showAsPushed: boolean;
  metaAdId: string | null;
  metaAdsetId: string | null;
  /** Operator-facing per-ad success or error text. */
  message: string;
  tone: MetaPushTone;
};

const QUIET_SUCCESS = new Set(['pushed', 'synced', 'ok', 'success']);

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function messageOf(value: unknown): string {
  const direct = text(value);
  if (direct) return direct;
  if (value && typeof value === 'object') {
    const rec = value as Record<string, unknown>;
    return text(rec.message) || text(rec.error) || text(rec.reason);
  }
  return '';
}

function metaIdOf(row: MetaPushPerAd): string | null {
  return text(row.metaAdId) || text(row.existingMetaAdId) || null;
}

function statusOf(row: MetaPushPerAd): string {
  return text(row.status) || text(row.code);
}

/** `already_pushed` → "Already Pushed"; `missing_landing_url` → "Missing Landing URL". */
export function humanizeStatus(status: string): string {
  const ACRONYMS = new Set(['url', 'id', 'meta', 'qc']);
  return status
    .trim()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(word => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function isFreshPush(row: MetaPushPerAd): boolean {
  if (row.ok === true) return true;
  if (row.ok === false) return false;
  const status = statusOf(row).toLowerCase();
  if (QUIET_SUCCESS.has(status)) return true;
  return text(row.metaSyncStatus).toLowerCase() === 'synced';
}

function showsAsPushed(row: MetaPushPerAd): boolean {
  if (isFreshPush(row)) return true;
  if (statusOf(row).toLowerCase() === 'already_pushed') return true;
  return text(row.metaSyncStatus).toLowerCase() === 'synced';
}

function describePerAd(row: MetaPushPerAd): string {
  const fresh = isFreshPush(row);
  const status = statusOf(row);
  const statusLabel = status ? humanizeStatus(status) : '';
  const explicitRaw = fresh
    ? (messageOf(row.message) || messageOf(row.error) || messageOf(row.reason))
    : (messageOf(row.error) || messageOf(row.message) || messageOf(row.reason));
  const snake = /^[a-z0-9]+(?:_[a-z0-9]+)+$/i;
  let explicit = explicitRaw;
  if (explicit && status && explicit.toLowerCase() === status.toLowerCase()) {
    explicit = statusLabel;
  } else if (explicit && !status && snake.test(explicit)) {
    explicit = humanizeStatus(explicit);
  }

  const parts: string[] = [];
  if (explicit) parts.push(explicit);
  else if (statusLabel) parts.push(statusLabel);
  else parts.push(fresh ? 'Pushed to Meta' : 'Unknown error from Meta');

  const blob = parts.join(' ').toLowerCase();
  const quiet = !!status && QUIET_SUCCESS.has(status.toLowerCase());
  if (
    statusLabel &&
    !quiet &&
    !blob.includes(statusLabel.toLowerCase()) &&
    !blob.includes(status.toLowerCase())
  ) {
    parts.push(statusLabel);
  }

  const id = metaIdOf(row);
  if (id && !parts.join(' ').includes(id)) parts.push(`Meta ad ${id}`);
  return parts.join(' — ');
}

function rowsOf(result: MetaPushResponse | null | undefined): MetaPushPerAd[] {
  if (!result || !Array.isArray(result.perAd)) return [];
  return result.perAd.filter(row => !!row && typeof row === 'object');
}

/**
 * Read one ad out of a push-to-meta response.
 * A payload that only has `results` (the field this modal used to read)
 * is a miss: the ad was not pushed from this client's point of view.
 */
export function interpretMetaPush(
  result: MetaPushResponse | null | undefined,
  adId: string
): MetaPushInterpretation {
  const rows = rowsOf(result);
  const none: MetaPushInterpretation = {
    ok: false,
    showAsPushed: false,
    metaAdId: null,
    metaAdsetId: null,
    message: 'Meta did not return a per-ad result.',
    tone: 'error'
  };
  if (rows.length === 0) return none;

  const match = rows.find(row => text(row.adId) === adId);
  // This modal pushes a single ad. If the backend echoes one row under a
  // different id spelling, that row is still this ad.
  const row = match ?? (rows.length === 1 ? rows[0] : null);
  if (!row) {
    const detail = rows.map(describePerAd).join(' · ');
    return {
      ...none,
      message: `This ad was not in the push result. ${detail}`
    };
  }

  const fresh = isFreshPush(row);
  const pushed = showsAsPushed(row);
  const others = rows.filter(other => other !== row).map(describePerAd);
  const mine = describePerAd(row);
  const message = others.length ? `${mine} · ${others.join(' · ')}` : mine;

  return {
    ok: fresh,
    showAsPushed: pushed,
    metaAdId: metaIdOf(row),
    metaAdsetId: text(row.metaAdsetId) || null,
    message,
    tone: fresh ? 'success' : pushed ? 'warning' : 'error'
  };
}
